import { useEffect } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { BarraDeTopo } from './components/casca/BarraDeTopo.tsx';
import { Catalogo } from './routes/Catalogo.tsx';
import { ModuloRoute } from './routes/ModuloRoute.tsx';
import { CursoRoute } from './routes/CursoRoute.tsx';
import { LessonRoute } from './routes/LessonRoute.tsx';
import { sondarSessao, useIdentidade } from './state/identidade.ts';
import { sondarAcessos } from './state/acessos.ts';
import { iniciarSincronizacao, descarregarFila } from './state/sincronizacao.ts';

/**
 * Rotas definidas em código, não por ficheiros: evita o plugin de geração de
 * árvore de rotas e a etapa de compilação que ele traz. Com este punhado de
 * rotas, o ganho do sistema por ficheiros não paga o custo.
 *
 * A fronteira de rota não é conveniência de navegação — é onde a divisão de
 * código acontece, e é o que faz o orçamento de RNF-001 fechar (ADR 0002).
 *
 * As três rotas de lição são as três perguntas do estudante, por ordem: o que
 * existe, onde é que eu ia neste curso, e o exercício à minha frente. As de
 * conta ficam **sob procura** (ADR 0009, decisão 10): quem chega vem ver uma
 * lição, não entrar em conta nenhuma, e nenhum destes ecrãs está no caminho
 * de arranque.
 */

function Casca() {
  useEffect(() => {
    // Tudo depois da primeira pintura, nunca antes: um `await` disto no
    // arranque custava RNF-002 inteiro em 3G lenta. A sincronização liga os
    // espaços por conta e fica à escuta; a sonda corrige a identidade
    // presumida quando o servidor responder, e a seguir descarrega a fila.
    iniciarSincronizacao();
    void sondarSessao().then(() => {
      descarregarFila();
      // H-304 — o que já está pago, a seguir à identidade e só com conta.
      if (useIdentidade.getState().identidade) void sondarAcessos();
    });

    // ADR 0028 — a sonda repete-se, para o número de mensagens da barra não
    // ficar preso ao arranque: a cada cinco minutos com a página à vista, e
    // ao voltar a ela. Só com conta; a um visitante a sonda dizia 401 e mais
    // nada, e é rede a pagar por nada.
    const CADA_MS = 5 * 60_000;
    const sondar = () => {
      if (document.visibilityState !== 'visible') return;
      if (!useIdentidade.getState().identidade) return;
      void sondarSessao();
    };
    const temporizador = window.setInterval(sondar, CADA_MS);
    document.addEventListener('visibilitychange', sondar);
    return () => {
      window.clearInterval(temporizador);
      document.removeEventListener('visibilitychange', sondar);
    };
  }, []);

  const caminho = useRouterState({ select: (s) => s.location.pathname });
  // Dentro de uma lição não há barra nenhuma: a lição é um túnel — entra-se,
  // avança-se, sai-se (regra da casa, e é também o que o Mimo faz).
  const dentroDeLicao = caminho.startsWith('/licao/');

  return (
    <>
      {dentroDeLicao ? null : <BarraDeTopo comMarca={caminho === '/'} />}
      <Outlet />
    </>
  );
}

const rootRoute = createRootRoute({
  component: Casca,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Catalogo,
});

// ADR 0027 — a página do curso: as secções, as subsecções e os tópicos, com
// a secção seleccionada na query para poder ser ligada e partilhada. É a
// segunda pergunta do estudante («onde é que eu ia») e por isso vive no
// pacote inicial, no lugar que era da página do módulo.
const cursoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/curso/$trackId',
  validateSearch: (procura: Record<string, unknown>): { seccao?: string } =>
    typeof procura['seccao'] === 'string' ? { seccao: procura['seccao'] } : {},
  component: CursoRoute,
});

// O endereço antigo do módulo reencaminha para o curso (ADR 0027).
const moduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/modulo/$moduleId',
  component: ModuloRoute,
});

const lessonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licao/$lessonId',
  component: LessonRoute,
});

const entrarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entrar',
  // H-304 — «voltar» é o caminho para onde se regressa depois de entrar (a
  // página de preços com a oferta escolhida). Só caminhos da própria
  // aplicação: um endereço absoluto seria uma porta para reencaminhar quem
  // entra para outro sítio.
  validateSearch: (procura: Record<string, unknown>): { voltar?: string } =>
    typeof procura['voltar'] === 'string' && /^\/(?!\/)/.test(procura['voltar'])
      ? { voltar: procura['voltar'] }
      : {},
  component: lazyRouteComponent(() => import('./conta/Entrar.tsx'), 'Entrar'),
});

const registarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/registar',
  component: lazyRouteComponent(() => import('./conta/Registar.tsx'), 'Registar'),
});

const confirmarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirmar',
  // A ligação de confirmação traz o testemunho na query; tudo o resto é lixo.
  validateSearch: (procura: Record<string, unknown>): { testemunho?: string } =>
    typeof procura['testemunho'] === 'string' ? { testemunho: procura['testemunho'] } : {},
  component: lazyRouteComponent(() => import('./conta/Confirmar.tsx'), 'Confirmar'),
});

const contaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/conta',
  component: lazyRouteComponent(() => import('./conta/Conta.tsx'), 'Conta'),
});

// H-202 — preços e pagamentos, sob procura como a conta: quem chega vem ver
// uma lição, e o checkout não tem nada que estar no caminho de arranque.
const precosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/precos',
  // H-304 — a oferta em destaque, para a intenção sobreviver à entrada.
  validateSearch: (procura: Record<string, unknown>): { oferta?: string } =>
    typeof procura['oferta'] === 'string' ? { oferta: procura['oferta'] } : {},
  component: lazyRouteComponent(() => import('./pagamentos/Precos.tsx'), 'Precos'),
});

const pagamentosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pagamentos',
  component: lazyRouteComponent(() => import('./pagamentos/Pagamentos.tsx'), 'Pagamentos'),
});

// ADR 0028 — as mensagens, sob procura como a conta: a barra só precisa do
// número, e esse viaja em `/api/eu`.
const mensagensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mensagens',
  component: lazyRouteComponent(() => import('./mensagens/Mensagens.tsx'), 'Mensagens'),
});

// H-141 — o detalhe do percurso, sob procura: quem não entra num percurso
// não paga o ecrã dele.
const percursoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/percurso/$pathId',
  component: lazyRouteComponent(() => import('./routes/Percurso.tsx'), 'Percurso'),
});

// H-171 — a área do tutor, sob procura: quem não tem turmas não paga o ecrã.
const turmasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/turmas',
  component: lazyRouteComponent(() => import('./routes/Turmas.tsx'), 'Turmas'),
});

// H-012 — recuperar e redefinir a palavra-passe, sob procura como o resto da conta.
const recuperarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recuperar',
  component: lazyRouteComponent(() => import('./conta/Recuperar.tsx'), 'Recuperar'),
});

const redefinirRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/redefinir',
  validateSearch: (procura: Record<string, unknown>): { testemunho?: string } =>
    typeof procura['testemunho'] === 'string' ? { testemunho: procura['testemunho'] } : {},
  component: lazyRouteComponent(() => import('./conta/Redefinir.tsx'), 'Redefinir'),
});

// E21 — os certificados e a verificação pública, sob procura. A verificação
// abre sem sessão: é para quem recebeu o código, não só para o titular.
const certificadosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/certificados',
  component: lazyRouteComponent(() => import('./routes/Certificados.tsx'), 'Certificados'),
});

const verificarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verificar/$codigo',
  component: lazyRouteComponent(() => import('./routes/Verificar.tsx'), 'Verificar'),
});

// E18 — a área do coordenador, sob procura, pela porta do estudante.
const instituicaoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/instituicao',
  component: lazyRouteComponent(() => import('./routes/Instituicao.tsx'), 'Instituicao'),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  cursoRoute,
  moduleRoute,
  lessonRoute,
  percursoRoute,
  turmasRoute,
  entrarRoute,
  registarRoute,
  confirmarRoute,
  contaRoute,
  precosRoute,
  pagamentosRoute,
  mensagensRoute,
  certificadosRoute,
  verificarRoute,
  instituicaoRoute,
  recuperarRoute,
  redefinirRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: false });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
